/**
 * Notification vocabulary — US-101849 (Notifications - Web) and US-113356 (Notification
 * Preferences - Web).
 *
 * The story's Section D fixes ~30 event titles and descriptions, but every one of them is
 * sourced "System per type" and marked non-editable, so the **server** composes the copy and
 * this app renders it verbatim. What the client genuinely owns is the two things the copy does
 * not carry: which settings category an event belongs to, and where clicking it navigates.
 */

/** The event families the story groups notifications into (Section D's numbered blocks). */
export const NOTIFICATION_GROUP = {
  /** 1a–1d — turn-on/turn-off success and failure, from any source. */
  COMMAND: 'command',
  /** 2a–2e — activation, expiry, expiring-soon, and the grouped multi-device variants. */
  SUBSCRIPTION: 'subscription',
  /** 3a–3d — support tickets. */
  SUPPORT: 'support',
  /** 3e–3g, 3k — warranty claims, including Warranty Expired. */
  WARRANTY: 'warranty',
  /** 3h–3j — replacement requests. */
  REPLACEMENT: 'replacement',
  /** 4a–4i — battery, fuel, water level, movement, offline. Never suppressible. */
  ALERT: 'alert',
  /** 5a–5c — device added, setup complete, QC complete. */
  LIFECYCLE: 'lifecycle',
  /** 6a — 200 hours of run time reached. */
  MAINTENANCE: 'maintenance',
} as const;

export type NotificationGroup = (typeof NOTIFICATION_GROUP)[keyof typeof NOTIFICATION_GROUP];

export const NOTIFICATION_GROUPS = Object.values(NOTIFICATION_GROUP);

/**
 * The backend's own event enum, and how its ~31 values collapse onto the eight
 * {@link NOTIFICATION_GROUP} families above.
 *
 * The wire field is **`typeCode`**, not `type` — confirmed against the live API on 2026-08-28
 * (greenway_mobile/docs/MOCKS.md §8) and mirrored from Tracking Mobile's `NotificationTypeCode`
 * so web and mobile can never disagree about which toggle governs an event. The enum itself was
 * read off `TestPushDto`, the only place the published spec lists it.
 *
 * Anything unrecognised degrades to `alert`, which is the safe direction: a new backend event
 * stays visible and un-suppressible rather than being hidden by a preference it was never
 * mapped to.
 */
export const GROUP_BY_TYPE_CODE: Readonly<Record<string, NotificationGroup>> = {
  COMMAND_EXECUTED: NOTIFICATION_GROUP.COMMAND,
  COMMAND_FAILED: NOTIFICATION_GROUP.COMMAND,

  SUBSCRIPTION_ACTIVATED: NOTIFICATION_GROUP.SUBSCRIPTION,
  SUBSCRIPTION_EXPIRED: NOTIFICATION_GROUP.SUBSCRIPTION,
  SUBSCRIPTION_EXPIRING_SOON: NOTIFICATION_GROUP.SUBSCRIPTION,
  // The grouped multi-device variants land on the same subscriptions page (§ NAVIGATION), so web
  // does not need mobile's single/grouped split.
  SUBSCRIPTION_EXPIRED_GROUPED: NOTIFICATION_GROUP.SUBSCRIPTION,
  SUBSCRIPTION_EXPIRING_SOON_GROUPED: NOTIFICATION_GROUP.SUBSCRIPTION,

  SUPPORT_CREATED: NOTIFICATION_GROUP.SUPPORT,
  SUPPORT_ASSIGNED: NOTIFICATION_GROUP.SUPPORT,
  SUPPORT_REPLIED: NOTIFICATION_GROUP.SUPPORT,
  SUPPORT_CLOSED: NOTIFICATION_GROUP.SUPPORT,

  WARRANTY_CREATED: NOTIFICATION_GROUP.WARRANTY,
  WARRANTY_APPROVED: NOTIFICATION_GROUP.WARRANTY,
  WARRANTY_REJECTED: NOTIFICATION_GROUP.WARRANTY,
  WARRANTY_EXPIRED: NOTIFICATION_GROUP.WARRANTY,

  REPLACEMENT_CREATED: NOTIFICATION_GROUP.REPLACEMENT,
  REPLACEMENT_APPROVED: NOTIFICATION_GROUP.REPLACEMENT,
  REPLACEMENT_REJECTED: NOTIFICATION_GROUP.REPLACEMENT,

  DEVICE_ADDED: NOTIFICATION_GROUP.LIFECYCLE,
  DEVICE_SETUP_COMPLETE: NOTIFICATION_GROUP.LIFECYCLE,
  DEVICE_QC_COMPLETE: NOTIFICATION_GROUP.LIFECYCLE,

  MAINTENANCE_200_HOURS: NOTIFICATION_GROUP.MAINTENANCE,

  // Every remaining `ALERT_*` — low battery, low fuel, water level, movement, disconnected —
  // falls through to the `alert` default below rather than being listed one by one, because the
  // enum's alert half is the part most likely to grow.
};

/**
 * The group for one wire `typeCode`.
 *
 * Falls back to the older `type` spelling first (the superseded PROPOSED contract sent lowercase
 * group names directly, and the MSW fixtures for the preferences seam still do), then to `alert`.
 */
export function groupForTypeCode(code: string | null | undefined): NotificationGroup {
  if (!code) return NOTIFICATION_GROUP.ALERT;
  const mapped = GROUP_BY_TYPE_CODE[code];
  if (mapped) return mapped;
  const lowered = code.toLowerCase();
  return (NOTIFICATION_GROUPS as readonly string[]).includes(lowered)
    ? (lowered as NotificationGroup)
    : NOTIFICATION_GROUP.ALERT;
}

/**
 * The four toggleable preference categories (US-113356 § A).
 *
 * Alerts are deliberately absent: they have no toggle and are always delivered (§ B).
 */
export const NOTIFICATION_CATEGORY = {
  COMMANDS: 'commands',
  SUBSCRIPTION: 'subscription',
  REQUESTS_AND_CLAIMS: 'requestsAndClaims',
  DEVICE_LIFECYCLE: 'deviceLifecycle',
} as const;

export type NotificationCategory =
  (typeof NOTIFICATION_CATEGORY)[keyof typeof NOTIFICATION_CATEGORY];

/**
 * Which category governs each group (US-113356 § A "TOGGLE MAPPING").
 *
 * `alert` maps to nothing on purpose — it is the always-delivered family, and the absence here
 * is what keeps it out of the drawer.
 */
export const CATEGORY_BY_GROUP: Partial<Record<NotificationGroup, NotificationCategory>> = {
  [NOTIFICATION_GROUP.COMMAND]: NOTIFICATION_CATEGORY.COMMANDS,
  [NOTIFICATION_GROUP.SUBSCRIPTION]: NOTIFICATION_CATEGORY.SUBSCRIPTION,
  [NOTIFICATION_GROUP.SUPPORT]: NOTIFICATION_CATEGORY.REQUESTS_AND_CLAIMS,
  [NOTIFICATION_GROUP.WARRANTY]: NOTIFICATION_CATEGORY.REQUESTS_AND_CLAIMS,
  [NOTIFICATION_GROUP.REPLACEMENT]: NOTIFICATION_CATEGORY.REQUESTS_AND_CLAIMS,
  [NOTIFICATION_GROUP.LIFECYCLE]: NOTIFICATION_CATEGORY.DEVICE_LIFECYCLE,
  [NOTIFICATION_GROUP.MAINTENANCE]: NOTIFICATION_CATEGORY.DEVICE_LIFECYCLE,
};

/** Drawer copy, verbatim from US-113356 § A. Order is the design's (Figma 625:576366). */
export const NOTIFICATION_CATEGORY_COPY: ReadonlyArray<{
  category: NotificationCategory;
  title: string;
  description: string;
}> = [
  {
    category: NOTIFICATION_CATEGORY.COMMANDS,
    title: 'Commands',
    description:
      'Get notified when a turn-on or turn-off command succeeds or fails on your devices.',
  },
  {
    category: NOTIFICATION_CATEGORY.SUBSCRIPTION,
    title: 'Subscription',
    description: 'Updates on subscription activation, expiry, and upcoming renewals.',
  },
  {
    category: NOTIFICATION_CATEGORY.REQUESTS_AND_CLAIMS,
    title: 'Requests & Claims',
    description: 'Updates on your support requests, warranty claims, and replacement requests.',
  },
  {
    category: NOTIFICATION_CATEGORY.DEVICE_LIFECYCLE,
    title: 'Device Lifecycle',
    description:
      'Updates when a device is added, configured, passes quality check, or reaches a maintenance milestone.',
  },
];

/** Fixed copy the stories pin exactly. Kept together so a wording change is one edit. */
export const NOTIFICATION_COPY = {
  /** US-101849 § ENTRY & EMPTY STATE. */
  EMPTY_TITLE: 'No notifications yet',
  EMPTY_BODY: "You're all caught up - new notifications will show up here.",
  /** § REFRESH / LOAD / SCROLL — a toast, and explicitly no retry control. */
  LOAD_FAILED: 'Failed to load notifications. Try again.',
  /** US-113356 § B — the always-delivered banner, verbatim including its full stop. */
  ALERTS_ALWAYS_DELIVERED:
    'Critical device alerts (low battery, low fuel, movement, critical water level, offline) are always delivered.',
  /** § B — the footer the AC asks for *alongside* the banner, not instead of it. */
  SAFETY_CRITICAL_FOOTER: 'Safety-critical alerts are always delivered',
  /** § C — save failure. */
  SETTINGS_SAVE_FAILED: "Something went wrong. Your notification preference wasn't saved.",
  /**
   * Load failure. The story pins no copy for it, so this is the house `<RegionError>` voice — the
   * same notice channel the Map's layers use.
   */
  SETTINGS_LOAD_FAILED: "We couldn't load your notification preferences. Please try again.",
} as const;

/** § C — per-category auto-save toasts. `{Category}` is the toggle's own title. */
export function categoryToggleToast(title: string, enabled: boolean): string {
  return enabled ? `${title} notifications enabled.` : `${title} notifications turned off.`;
}

export const NOTIFICATION_POLICY = {
  /** Inline scrolling, no pagination controls (§ REFRESH / LOAD / SCROLL). */
  PAGE_SIZE: 20,
  /** Load the next batch once the scroller is within this many px of the bottom. */
  SCROLL_THRESHOLD: 48,
} as const;
